import React, { Component, Fragment } from "react";
import { Link } from "react-router-dom";
import StatusDataLoader from "../../services/StatusDataLoader";
import { StatusData } from "../../shared/types";
import CurrencyCardArea from "../CurrencyCardArea/CurrencyCardArea";
import StatsArea from "../StatsArea/StatsArea";
import ContentArea from "../ContentArea/ContentArea";
import Layout from "./Layout/Layout";
import LoadingOverlay from "./LoadingOverlay/LoadingOverlay";
import "./Style.css";
import StatusMessage from "../StatusMessage/StatusMessage";
import { useSeo } from "../../utils/Hooks/useSeo";
import {
  HOME_COMPARISON_ITEMS,
  getCompactBtcLabel,
  getCompactUsdLabel,
  getHomeComparisonInBtc,
} from "../../utils/Calculators/ConversionPerspectiveCalculator";
import { CONVERSION_REFERENCE_DATA } from "../../utils/Calculators/ConversionReferenceData";

type Props = {};
type State = {
  status: StatusData;
  areReferenceSectionsOpen: boolean;
};

const HOME_REFERENCE_SECTIONS_STORAGE_KEY = "in-btc:home-reference-sections-open";

class HomePageContent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      status: undefined,
      areReferenceSectionsOpen: this.getStoredReferenceSectionsState(),
    };
  }

  async componentDidMount() {
    this.setState({ status: await StatusDataLoader.getStatusData() });
  }

  getStoredReferenceSectionsState = () => {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      return window.localStorage.getItem(HOME_REFERENCE_SECTIONS_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  };

  toggleReferenceSections = () => {
    this.setState(
      ({ areReferenceSectionsOpen }) => ({
        areReferenceSectionsOpen: !areReferenceSectionsOpen,
      }),
      () => {
        try {
          window.localStorage.setItem(
            HOME_REFERENCE_SECTIONS_STORAGE_KEY,
            String(this.state.areReferenceSectionsOpen),
          );
        } catch {
          // Browsers can block storage; the toggle should still work for this visit.
        }
      },
    );
  };

  render() {
    const { status, areReferenceSectionsOpen } = this.state;
    const shouldShowLoading = status === undefined;
    const isLoaded = !shouldShowLoading;
    const btcPrice = status?.market?.btcPriceUsd ?? 0;
    const referencePanelClassName = areReferenceSectionsOpen
      ? "home-reference-panel is-open"
      : "home-reference-panel";

    return (
      <Fragment>
        <LoadingOverlay isVisible={shouldShowLoading}></LoadingOverlay>
        <Layout>
          <div className="home-hero-shell">
            <section className="home-hero-card">
              <div className="home-hero-top">
                <div>
                  <p>
                    See crypto and real-world value in Bitcoin terms: long-term performance
                    versus BTC, distance from BTC highs, market caps, everyday prices, and key
                    network signals.
                  </p>
                </div>
                <button
                  type="button"
                  className={isLoaded ? "home-reference-toggle is-loaded" : "home-reference-toggle"}
                  aria-expanded={areReferenceSectionsOpen}
                  aria-controls="home-reference-sections"
                  aria-label={
                    areReferenceSectionsOpen
                      ? "Hide network and perspective context"
                      : "Show network and perspective context"
                  }
                  onClick={this.toggleReferenceSections}
                >
                  <span className="home-reference-toggle-label">More insights</span>
                  <span className="home-reference-switch" aria-hidden="true">
                    <span className="home-reference-switch-thumb" />
                  </span>
                </button>
              </div>
            </section>
          </div>

          <div
            id="home-reference-sections"
            className={referencePanelClassName}
            aria-hidden={!areReferenceSectionsOpen}
          >
            <div className="home-reference-panel-inner">
              <StatsArea status={status} />
              <br />
              <ContentArea
                label="Quick perspective"
                topHeaderClassName="quick-perspective-header"
                topRightAction={{ label: "Open converter", href: "/convert" }}
              >
                <div className="home-snapshot">
                  <div className="home-snapshot-header">
                    <div>
                      <p>
                        Real-time BTC price to anchor some heavyweight USD figures. Want a custom
                        amount? Jump into the converter.
                      </p>
                    </div>
                  </div>

                  {!btcPrice ? (
                    <StatusMessage
                      title="Waiting on market data."
                      description="Refresh in a moment to load the conversion snapshot."
                      minHeightRem={6}
                    />
                  ) : (
                    <div className="home-snapshot-grid">
                      {HOME_COMPARISON_ITEMS.map((item) => {
                        const btcEquivalent = getHomeComparisonInBtc(
                          item.usdValue,
                          btcPrice,
                        );
                        const btcMarketCap = status?.market?.btcMarketCapUsd ?? 0;
                        const marketCapMultiple =
                          btcMarketCap > 0 ? item.usdValue / btcMarketCap : 0;

                        return (
                          <div className="home-snapshot-card" key={item.id}>
                            <div className="snapshot-label">{item.label}</div>
                            <div className="snapshot-btc">
                              {btcEquivalent ? getCompactBtcLabel(btcEquivalent) : "--"}
                            </div>
                            <div className="snapshot-usd">
                              {getCompactUsdLabel(item.usdValue)} =
                              <span className="snapshot-mcap">
                                {btcMarketCap > 0
                                  ? `${marketCapMultiple.toFixed(1)}x BTC MCap`
                                  : "--"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="home-snapshot-footnote">
                    Based on data available on {CONVERSION_REFERENCE_DATA.dataAsOf}.
                  </div>
                </div>
              </ContentArea>
              <br />
            </div>
          </div>
          <CurrencyCardArea />
          <br />
        </Layout>
      </Fragment>
    );
  }
}

export const HomePage = () => {
  useSeo({
    title: "In Bitcoin Terms",
    description:
      "Explore cryptocurrencies and real-world assets through a Bitcoin lens. Track long-term crypto performance against Bitcoin, convert BTC and USD values, and understand what they represent in real-world terms. The dashboard also includes key network metrics, issuance, and inflation insights for added context.",
    canonicalPath: "/",
  });

  return <HomePageContent />;
};
